/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dayjs } from 'dayjs'
import _ from 'lodash'
import { useState } from 'react'

import { attributesSelectors } from '@/entities/attributes'
import { AttributeTypeEnum } from '@/entities/attributes/models/Attribute'
import { depotsSelectors } from '@/entities/depots'
import { saveOrder } from '@/entities/orders/thunks/saveOrder'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { getFormDefinitionForCourierModel } from '@/fragments/OrderFormDefinitions/getFormDefinitionForCourierModel'
import { getFormDefinitionForDepotModel } from '@/fragments/OrderFormDefinitions/getFormDefinitionForDepotModel'
import { Modal } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { SelectOption } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { OrderItemUpdate } from '@/shared/components/OrderItemsField/models/OrderItemUpdate'
import { OrderItemChangeStateEnum } from '@/shared/components/OrderItemsField/models/OrderItemUpdateState'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { timeDayjsToHhMm, timeDayjsToHhMmSs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { styled } from '@mui/material'

export type AddOrderDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const AddOrderDialog: React.FC<AddOrderDialogProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch()
  const businessModel = useAppSelector((state) => state.user.businessModel)
  const isCourierModel = businessModel === BusinessModelEnum.Courier
  const isDepotModel = businessModel === BusinessModelEnum.Depot
  const [isSubmitting, setSubmitting] = useState(false)
  const { setToastContent } = useToastContext()

  const activeGroupId = useAppSelector((state) => state.userInterface.activeGroupId)
  const depots = useAppSelector((state) => depotsSelectors.selectAll(state))
    .filter((depot) => depot.groupId === activeGroupId)
  const ordersAttributes = useAppSelector((state) => attributesSelectors.selectAll(state))
    .filter((attribute) => attribute.attributeType === AttributeTypeEnum.Order)

  // Retreive the form definition based on the business model.
  //   The form definition contains the fields of the form used for DynamicFormRenderer.
  const depotModelFormDefinition = getFormDefinitionForDepotModel({
    defaultValues: {},
    selectOptions: {
      'depot': depots.map((depot) => ({ label: depot.name, value: depot.id })),
      'attributes': ordersAttributes.map((attribute) => ({ label: attribute.name, value: attribute.id })),
    },
  })

  const courierModelFormDefinition = getFormDefinitionForCourierModel({
    defaultValues: {},
    selectOptions: {
      'attributes': ordersAttributes.map((attribute) => ({ label: attribute.name, value: attribute.id })),
    },
  })
  const formDefinition = isCourierModel ? courierModelFormDefinition : depotModelFormDefinition

  const onSubmit = (data: Record<string, unknown>) => {
    setSubmitting(true)

    const postVal = _.cloneDeep(data)

    // Make API call to create order after making the relevant data transformations
    postVal.isPickup = data.isPickup === '1'
    postVal.orderItems = []
    postVal.attributeIds = (data.attributes as SelectOption[])?.map((attribute) => attribute.value)

    const depotId = (data.depot as SelectOption)?.value

    // Process order items
    if (data.orderItems) {
      postVal.orderItems = (data.orderItems as OrderItemUpdate[]).map((orderItem) => {
        if (orderItem.state === OrderItemChangeStateEnum.Added) {
          const { id, ...newOrderItem } = orderItem

          return newOrderItem
        }

        return orderItem
      })
    }

    // For Depot Model, there is only one customer address
    if (isDepotModel) {
      // Time window
      const timeWindows = []

      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStart as Dayjs),
        twStartNextDay: data.twStartNextDay,
        twFinish: timeDayjsToHhMm(data.twFinish as Dayjs),
        twFinishNextDay: data.twFinishNextDay,
        isPickup: data.isPickup,
      })
      postVal.timeWindows = timeWindows

      // From and to Addresses
      if (postVal.isPickup) {
        postVal.serviceTimePickup = timeDayjsToHhMmSs(data.serviceTime as Dayjs)
        postVal.fromLocation = postVal.address
        if (postVal.fromLocation)
          (postVal.fromLocation as any).externalId = postVal.customerExternalId
        postVal.toLocation = depots.find((depot) => depot.id === depotId)
      } else {
        postVal.serviceTimeDelivery = timeDayjsToHhMmSs(data.serviceTime as Dayjs)
        postVal.fromLocation = depots.find((depot) => depot.id === depotId)
        postVal.toLocation = postVal.address
        if (postVal.toLocation)
          (postVal.toLocation as any).externalId = postVal.customerExternalId
      }

      delete postVal.attributes
      delete postVal.depot
      delete postVal.address
      delete postVal.customerExternalId
      delete postVal.twStart
      delete postVal.twStartNextDay
      delete postVal.twFinish
      delete postVal.twFinishNextDay
    }

    // For Courier Model, there are two addresses. One for pickup and one for delivery.
    if (isCourierModel) {
      const timeWindows = []

      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStartPickup as Dayjs),
        twStartNextDay: data.twStartNextDayPickup,
        twFinish: timeDayjsToHhMm(data.twFinishPickup as Dayjs),
        twFinishNextDay: data.twFinishNextDayPickup,
        isPickup: true,
      })
      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStartDelivery as Dayjs),
        twStartNextDay: data.twStartNextDayDelivery,
        twFinish: timeDayjsToHhMm(data.twFinishDelivery as Dayjs),
        twFinishNextDay: data.twFinishNextDayDelivery,
        isPickup: false,
      })
    }

    // Make API call to create order and handle the response
    dispatch(saveOrder({ order: postVal }))
      .unwrap()
      .then((newOrderResult) => {
        if (newOrderResult) {
          setToastContent({
            open: true,
            severity: 'success',
            title: 'Order Added',
            content: `Order ${newOrderResult.orderNumber} has been added successfully.`,
          })
          setSubmitting(false)
          onClose()
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const onCancel = () => {
    onClose()
  }

  const scrollToTop = () => {
    const node = document.querySelector('#__modal_main')

    node?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  return (
    <StyledModal
      title={'Add Order'}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      // disableClose={isLoading}
      disabledBackdropClose
    >
      <StyledContent>
        <DynamicForm
          {...formDefinition}
          onSubmit={onSubmit}
          onCancel={onCancel}
          onStepperNavigate={scrollToTop}
          sx={{ height: '100%' }}
          slotProps={{
            submitButton: {
              disabled: isSubmitting,
            },
          }}
        >
        </DynamicForm>
      </StyledContent>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  > div {
    height: 100%;
  }
`

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    max-width: ${(props) => props.theme.typography.pxToRem(540)};
    height: 100%;
  }
`
